import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCourse } from '@/hooks/useCourses'
import { useModulesByCourse } from '@/hooks/useModules'
import { useAuth } from '@/hooks/useAuth'
import { useEnrollments, useEnrollInCourse } from '@/hooks/useEnrollments'
import { useInstructorSplits } from '@/hooks/useInstructorSplits'
import { CheckoutModal, LoginModal } from '@/components/modals'
import { ModuleSyllabus } from '@/components/lessons'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { useForceLightMode } from '@/contexts/ThemeContext'
import { BulkCheckoutModal } from '@/components/checkout/BulkCheckoutModal'
import { useBatches } from '@/hooks/useBatches'

export const CourseDetailPage: React.FC = () => {
  useForceLightMode()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()

  // Data Fetching
  const { data: course, isLoading: isLoadingCourse, error: courseError } = useCourse(id || '')
  const { data: modules, isLoading: isLoadingSyllabus } = useModulesByCourse(id || '')
  const { data: instructors } = useInstructorSplits(id || '')
  const primaryInstructor = instructors?.[0]

  // Batch data for hybrid courses (to check availability)
  const isHybridCourse = course?.courseType === 'hybrid'
  const { data: batchesData } = useBatches(isHybridCourse ? (id || '') : '')
  const activeBatches = isHybridCourse ? (batchesData?.data || []).filter((b: any) => b.status === 'active') : []

  const isBatchAvailableForEnrollment = (batch: any): boolean => {
    const isFull = (batch.enrolledCount || 0) >= batch.capacity
    const isDeadlineClosed = batch.registrationDeadline && new Date(batch.registrationDeadline) < new Date()
    const isExpired = new Date(batch.endDate) < new Date()
    return !isFull && !isDeadlineClosed && !isExpired
  }

  const availableBatches = activeBatches.filter(isBatchAvailableForEnrollment)
  const hasActiveBatches = !isHybridCourse || availableBatches.length > 0

  // Enrollment Logic
  const { data: enrollments } = useEnrollments({ userId: user?.id })
  const enrollMutation = useEnrollInCourse()

  const isEnrolled = enrollments?.some((e: any) => e.courseId === id && e.status === 'active')
  const enrollment = enrollments?.find((e: any) => e.courseId === id)

  // UI State
  const [showCheckout, setShowCheckout] = useState(false)
  const [showBulkCheckout, setShowBulkCheckout] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [openSubjectId, setOpenSubjectId] = useState<string | null>(modules?.[0]?.id || null)

  const handleStart = async () => {
    if (!isAuthenticated) {
      setShowLogin(true)
      return
    }

    if (isEnrolled && enrollment) {
      navigate(`/learn/${enrollment.id}`)
      return
    }

    // Check if course is published
    if (!course?.isPublished) {
      alert('Course belum tersedia. Tunggu sampai status berubah menjadi "Tersedia"')
      return
    }

    // Hybrid course — must select a batch first
    if (course?.courseType === 'hybrid') {
      navigate(`/course/${id}/batches`)
      return
    }

    if (course?.price === 0) {
      // Free pure_online course - enroll directly
      try {
        const result = await enrollMutation.mutateAsync({ courseId: id! })
        const enrollmentData = result.data
        if (enrollmentData) {
          navigate(`/learn/${enrollmentData.id}`)
        }
      } catch (error) {
        console.error("Enrollment failed", error)
        alert('Gagal mendaftar ke course. Silakan coba lagi.')
      }
      return
    }

    // Paid pure_online course - show checkout
    setShowCheckout(true)
  }

  const getButtonText = () => {
    if (isEnrolled) return 'Lanjut Belajar'
    if (!course?.isPublished) return 'Segera Hadir'
    if (course?.courseType === 'hybrid') {
      if (!hasActiveBatches) return 'Belum Ada Batch Tersedia'
      return 'Pilih Jadwal Batch'
    }
    if (course?.price === 0) return 'Mulai Belajar'
    return 'Beli Sekarang'
  }

  if (isLoadingCourse) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-500 dark:text-text-muted">Loading course...</div>
      </div>
    )
  }

  if (courseError || !course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <span className="material-symbols-outlined text-6xl text-gray-500 dark:text-gray-400 mb-4">error</span>
        <p className="text-lg font-bold mb-2">Course not found</p>
        <button onClick={() => navigate('/discover')} className="text-primary font-bold hover:underline">
          Back to Courses
        </button>
      </div>
    )
  }

  const isHybrid = course.courseType === 'hybrid'
  const isFree = course.price === 0

    return (
        <div className="min-h-screen pb-32 md:pb-12 bg-slate-50 dark:bg-slate-950 relative">
            <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
            <CheckoutModal isOpen={showCheckout} onClose={() => setShowCheckout(false)} course={course} />
            {isAuthenticated && !isEnrolled && course.price > 0 && (
              <BulkCheckoutModal
                isOpen={showBulkCheckout}
                onClose={() => setShowBulkCheckout(false)}
                course={{ id: course.id, title: course.title, price: course.price }}
                buyer={{ email: user?.email || '', name: user?.name || '' }}
              />
            )}

            {/* Hero / Header */}
            <div className="relative h-72 md:h-[450px] w-full overflow-hidden">
                {course.thumbnailUrl ? (
                    <ImageWithFallback
                        src={course.thumbnailUrl}
                        alt={course.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 bg-slate-100 dark:bg-white/5 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[120px] text-slate-200 dark:text-slate-800">school</span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/20" />

                <div className="absolute top-0 left-0 z-10 w-full p-6 flex items-center justify-between pointer-events-none">
                    <button
                        onClick={() => navigate('/discover')}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all border border-white/10 pointer-events-auto"
                    >
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                if (navigator.share) {
                                    navigator.share({ title: document.title, url: window.location.href })
                                } else {
                                    navigator.clipboard.writeText(window.location.href)
                                }
                            }}
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all border border-white/10 pointer-events-auto"
                        >
                            <span className="material-symbols-outlined">share</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Container */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative -mt-16 md:-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    
                    {/* Main Content */}
                    <div className="lg:col-span-8 space-y-10">
                        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100 dark:border-white/5">
                            {/* Badges */}
                            <div className="mb-6 flex flex-wrap items-center gap-3">
                                {!isHybrid && (
                                  <span className={`px-3 py-1 rounded-lg text-[11px] font-bold ${isFree ? 'bg-primary/10 text-primary' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'}`}>
                                    {isFree ? 'Pendaftaran Gratis' : 'Kursus Premium'}
                                  </span>
                                )}
                                {isHybrid && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20">
                                        <span className="material-symbols-outlined text-[16px]">location_on</span> Hybrid
                                    </span>
                                )}
                                <div className="flex items-center gap-1.5 ml-auto">
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">group</span>
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                                        {course.enrollmentCount || 0}
                                    </span>
                                    <span className="text-sm font-medium text-slate-400">Siswa terdaftar</span>
                                </div>
                            </div>

                            {/* Hybrid info banner */}
                            {isHybrid && (
                                <div className="mb-8 flex gap-4 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-5">
                                    <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-[26px] shrink-0 mt-0.5">menu_book</span>
                                    <div>
                                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mb-1">Materi Teori Gratis</p>
                                        <p className="text-sm text-emerald-700/80 dark:text-emerald-300/80 leading-relaxed">
                                            Semua materi teori dapat diakses gratis. Daftar ke salah satu batch untuk mengikuti sesi praktik langsung dan mendapatkan sertifikat.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight leading-tight">
                                {course.title}
                            </h1>
                            <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed font-medium mb-10">
                                {course.description || 'Kurikulum komprehensif yang dirancang untuk peserta dengan kombinasi teori digital dan praktik langsung.'}
                            </p>

                            <div className="flex items-center gap-4 pt-8 border-t border-slate-50 dark:border-white/5">
                                <div className="size-12 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 font-bold border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden">
                                    <span className="text-xl">{primaryInstructor?.instructorName?.charAt(0) || 'I'}</span>
                                </div>
                                <div>
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Instruktur Utama</p>
                                    <p className="text-base font-bold text-slate-900 dark:text-white">
                                        {primaryInstructor?.instructorName || 'Instruktur Berpengalaman'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Curriculum Section */}
                        <div className="space-y-6">
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Kurikulum Kursus</h3>
                            <div className="space-y-4">
                                {isLoadingSyllabus ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} className="h-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 animate-pulse" />
                                    ))
                                ) : modules && modules.length > 0 ? (
                                    modules.map((module: any) => {
                                        const isOpen = openSubjectId === module.id
                                        return (
                                            <div key={module.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 overflow-hidden transition-all duration-300">
                                                <button
                                                    onClick={() => setOpenSubjectId(isOpen ? null : module.id)}
                                                    className={`w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors ${isOpen ? 'bg-slate-50/50 dark:bg-white/5' : ''}`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="size-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center">
                                                            <span className="material-symbols-outlined">menu_book</span>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-slate-900 dark:text-white leading-tight">{module.title}</h4>
                                                            <p className="text-xs text-slate-400 font-medium mt-0.5">{module.description || 'Pelajari detail modul ini'}</p>
                                                        </div>
                                                    </div>
                                                    <span className={`material-symbols-outlined text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                                        expand_more
                                                    </span>
                                                </button>

                                                {isOpen && (
                                                    <div className="px-6 pb-6 pt-2 border-t border-slate-50 dark:border-white/5">
                                                        <ModuleSyllabus
                                                            moduleId={module.id}
                                                            courseId={id || ''}
                                                            enrollmentId={enrollment?.id}
                                                            isEnrolled={!!isEnrolled}
                                                            isPaidCourse={course.price > 0}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-slate-200 dark:border-white/10">
                                        <p className="text-sm font-bold text-slate-400">Kurikulum belum tersedia saat ini.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar / Price Card */}
                    <div className="lg:col-span-4">
                        <div className="sticky top-32 space-y-6">
                            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-white/5 space-y-8">
                                <div className="space-y-1">
                                    <p className="text-sm font-bold text-slate-400">Harga Kursus</p>
                                    <div className="text-4xl font-bold text-slate-900 dark:text-white">
                                        {isHybrid
                                            ? (
                                                <div className="space-y-1">
                                                    <span className="text-lg font-bold text-slate-500 dark:text-slate-400">Harga per Batch</span>
                                                    <p className="text-xs font-medium text-slate-400 dark:text-slate-500">Pilih batch untuk lihat biaya</p>
                                                </div>
                                            )
                                            : isFree
                                                ? 'Gratis'
                                                : new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(course.price)
                                        }
                                    </div>
                                </div>
                                
                                <button
                                    onClick={handleStart}
                                    disabled={enrollMutation.isPending || !course?.isPublished || (isHybrid && !hasActiveBatches)}
                                    className="w-full py-4 bg-primary text-white rounded-2xl font-bold text-base hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
                                >
                                    {enrollMutation.isPending ? 'Memproses...' : getButtonText()}
                                </button>
                                {isAuthenticated && !isEnrolled && course.price > 0 && course.isPublished && (
                                  <button
                                    onClick={() => setShowBulkCheckout(true)}
                                    className="w-full py-3 border border-primary text-primary rounded-2xl font-bold text-sm hover:bg-primary/5 active:scale-95 transition-all"
                                  >
                                    Beli untuk Beberapa Orang
                                  </button>
                                )}
                                
                                <div className="space-y-4 pt-2">
                                    <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-primary">verified</span>
                                        <span>Akses Seumur Hidup</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-primary">workspace_premium</span>
                                        <span>Sertifikat Penyelesaian</span>
                                    </div>
                                    {isHybrid && (
                                        <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                                            <span className="material-symbols-outlined text-primary">groups</span>
                                            <span>Sesi Praktik Lapangan</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-primary">support_agent</span>
                                        <span>Bantuan Instruktur</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Bottom CTA */}
            <div className="lg:hidden fixed bottom-0 left-0 z-50 w-full bg-white/90 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-100 dark:border-white/5 px-6 py-5 pb-safe shadow-2xl">
                <div className="flex items-center justify-between gap-6">
                    <div className="space-y-0.5">
                        <p className="text-[11px] font-bold text-slate-400">Total Harga</p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white">
                            {isHybrid
                                ? 'Per Batch'
                                : isFree
                                    ? 'Gratis'
                                    : new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(course.price)
                            }
                        </p>
                    </div>
                    <button
                        onClick={handleStart}
                        disabled={enrollMutation.isPending || !course?.isPublished || (isHybrid && !hasActiveBatches)}
                        className="flex-1 h-14 bg-primary text-white rounded-2xl font-bold text-sm shadow-lg shadow-primary/20 active:scale-95 transition-all disabled:opacity-50"
                    >
                        {enrollMutation.isPending ? '...' : getButtonText()}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default CourseDetailPage
